/**
 * Funções HTTP do perfil — GET e PATCH /perfil.
 *
 * Estado das chaves de acesso ao provedor foi extraído em E2.F3 (2026-05-16)
 * para apps/web/src/lib/api_keys.ts (lê e escreve via /api-keys). PerfilPage
 * usa apenas o GET daqui + link para a tela /configuracoes/api-keys.
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';

import type { Ambiente, PerfilRole } from '@/lib/auth';

export interface PerfilData {
  id: string;
  email: string;
  escritorio_id: string | null;
  role: PerfilRole;
  /** E30.F1: nome do perito titular do escritório (Sidebar exibe; fallback e-mail). */
  perito_titular: string | null;
  /**
   * E34.F1/ADR-0046: quando `true`, o download do laudo exige homologação por
   * revisor (E23.F7) e o painel de homologação aparece. `false` (default da
   * config) libera o download após as narrativas e esconde o painel.
   */
  require_reviewer_approval: boolean;
  /**
   * E50.F2/ADR-0059: qual área da SPA é a casa do usuário (`interno` = área do
   * perito; `escritorio`/`banco` = portal). O backend passa a enviar o campo na
   * E50.F3; payloads antigos seguem válidos — `getPerfil` normaliza para
   * `interno` quando ausente.
   */
  ambiente: Ambiente;
  /**
   * E50.F6: `true` só para o escritório-plataforma — exibe a esteira do portal
   * (escopo clientes/certificações) na área do perito. Só exibição: a
   * autorização real vive no servidor (403 `PLATFORM_REQUIRED`). Normalizado
   * para `false` quando ausente (payloads antigos).
   */
  is_platform: boolean;
  /**
   * E57.F2 (ADR-0066): banco em cujo contexto a sessão está operando, já
   * conferido contra o vínculo no servidor. `null` = sem contexto — com mais de
   * um vínculo, o shell pede a escolha e nenhum caso de banco aparece até lá.
   * Fonte única: o que o servidor diz, não o que o navegador guardou.
   */
  banco_ativo: string | null;
  /**
   * E162.F2.T05 (QA-23): o NOME do banco em cujo contexto a sessão opera — o do
   * próprio banco para quem é do banco, o do contexto ativo para o escritório.
   * O portal o mostra o tempo todo. `null` = sem contexto.
   */
  banco_nome: string | null;
  /**
   * E52.F3: registro profissional cadastrado pelo próprio usuário. É impresso
   * no laudo certificado que ele assinar (ADR-0063 §5). `null` = não
   * cadastrado — e o laudo omite a linha, em vez de imprimir rótulo vazio.
   */
  registro_profissional: string | null;
  /**
   * E56.F1: nome de exibição da PESSOA (`users.display_name`). É o primeiro
   * degrau da identidade no shell — o `perito_titular` é do ESCRITÓRIO e vem
   * depois; o e-mail é o último recurso. `null` = não cadastrado.
   */
  display_name: string | null;
  /**
   * E65.F3 (ADR-0069): a senha vigente foi definida por terceiro e a troca ainda
   * não aconteceu. Quem RECUSA é o servidor (403 `SENHA_PROVISORIA_PENDENTE` em
   * toda rota fora da lista isenta) — este campo existe para a tela poder
   * explicar por quê, em vez de mostrar um erro sem causa. Normalizado para
   * `false` quando ausente.
   */
  senha_provisoria: boolean;
  /**
   * E164.F2 (ADR-0113): o CPF que a assinatura com certificado digital confere (só o próprio
   * usuário o vê) e a marca do responsável técnico, concedida pelo titular da plataforma.
   * Opcionais para os payloads antigos; `getPerfil` normaliza.
   */
  cpf?: string | null;
  responsavel_tecnico?: boolean;
}

export type PerfilErrorReason = 'validation' | 'network' | 'unknown';

export class PerfilError extends Error {
  public readonly reason: PerfilErrorReason;

  constructor(reason: PerfilErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'PerfilError';
  }
}

function _classify(err: unknown): PerfilError {
  if (axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError;
    if (axiosErr.response?.status === 422) {
      return new PerfilError('validation');
    }
    if (axiosErr.code === 'ERR_NETWORK' || axiosErr.code === 'ECONNABORTED') {
      return new PerfilError('network');
    }
  }
  return new PerfilError('unknown');
}

type _PerfilPayload = Omit<
  PerfilData,
  | 'ambiente'
  | 'is_platform'
  | 'registro_profissional'
  | 'display_name'
  | 'banco_ativo'
  | 'banco_nome'
  | 'senha_provisoria'
> & {
  ambiente?: Ambiente;
  is_platform?: boolean;
  banco_ativo?: string | null;
  banco_nome?: string | null;
  registro_profissional?: string | null;
  display_name?: string | null;
  senha_provisoria?: boolean;
};

/** Payload do servidor → contrato do front (campos novos com default seguro). */
function _normalizar(data: _PerfilPayload): PerfilData {
  return {
    ...data,
    ambiente: data.ambiente ?? 'interno',
    is_platform: data.is_platform ?? false,
    banco_ativo: data.banco_ativo ?? null,
    banco_nome: data.banco_nome ?? null,
    registro_profissional: data.registro_profissional ?? null,
    display_name: data.display_name ?? null,
    senha_provisoria: data.senha_provisoria ?? false,
    cpf: data.cpf ?? null,
    responsavel_tecnico: data.responsavel_tecnico ?? false,
  };
}

export async function getPerfil(client: AxiosInstance): Promise<PerfilData> {
  try {
    const { data } = await client.get<_PerfilPayload>('/perfil');
    return _normalizar(data);
  } catch (err) {
    throw _classify(err);
  }
}

/**
 * Atualiza o cadastro do PRÓPRIO usuário (registro profissional e, na E56.F1,
 * nome de exibição).
 *
 * String vazia é enviada como tal e o servidor a normaliza para "não
 * cadastrado" — limpar o campo é uma ação legítima, não um erro de validação.
 * O PATCH é parcial no servidor: campo AUSENTE do corpo não é tocado, então
 * mandar só o que mudou preserva o outro campo.
 */
export interface PerfilUpdatePayload {
  registro_profissional?: string | null;
  display_name?: string | null;
  cpf?: string | null;
}

export async function updatePerfil(
  client: AxiosInstance,
  payload: PerfilUpdatePayload,
): Promise<PerfilData> {
  try {
    const { data } = await client.patch<_PerfilPayload>('/perfil', payload);
    return _normalizar(data);
  } catch (err) {
    throw _classify(err);
  }
}

/** Um banco que a sessão pode escolher como contexto (E57.F2, ADR-0066). */
export interface BancoVinculo {
  id: string;
  nome: string;
}

/**
 * Bancos que o escritório da sessão atende.
 *
 * A rota vive sob `/perfil` porque a pergunta é sobre a SESSÃO, não sobre o
 * catálogo de bancos: listar vínculos fora da sessão autenticada deixaria
 * enumerar, por e-mail, com quais bancos um escritório trabalha.
 */
export async function listarBancosDoContexto(client: AxiosInstance): Promise<BancoVinculo[]> {
  const { data } = await client.get<BancoVinculo[]>('/perfil/bancos');
  return data;
}

/** Corpo do `POST /perfil/senha` (E65.F3, ADR-0069 §2.4). */
export interface TrocaDeSenhaPayload {
  senha_atual: string;
  senha_nova: string;
}

export type TrocaDeSenhaReason = 'senha_atual_incorreta' | 'validation' | 'network' | 'unknown';

export class TrocaDeSenhaError extends Error {
  public readonly reason: TrocaDeSenhaReason;

  constructor(reason: TrocaDeSenhaReason) {
    super(reason);
    this.reason = reason;
    this.name = 'TrocaDeSenhaError';
  }
}

/**
 * Troca a senha do PRÓPRIO usuário.
 *
 * A senha atual é exigida pelo servidor mesmo quando a vigente é a provisória —
 * ela é conhecida por quem criou a conta. A recusa vem como 403
 * `SENHA_ATUAL_INCORRETA` e é traduzida aqui por RAZÃO, nunca repassada como
 * código cru para a tela.
 */
export async function trocarSenha(
  client: AxiosInstance,
  payload: TrocaDeSenhaPayload,
): Promise<void> {
  try {
    await client.post('/perfil/senha', payload);
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const detail = (err.response?.data as { detail?: string } | undefined)?.detail;
      if (status === 403 && detail === 'SENHA_ATUAL_INCORRETA') {
        throw new TrocaDeSenhaError('senha_atual_incorreta');
      }
      if (status === 422) {
        throw new TrocaDeSenhaError('validation');
      }
      if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
        throw new TrocaDeSenhaError('network');
      }
    }
    throw new TrocaDeSenhaError('unknown');
  }
}
